import { Toast } from "@/components/Toasts/Toasts";
import { Toast as RadixToast } from "radix-ui";
import React, { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { v4 as uuidv4 } from "uuid";

type ToastProps = React.ComponentProps<typeof Toast>;

type Action = { type: "ADD_TOAST"; payload: ToastProps } | { type: "REMOVE_TOAST"; payload: { id: string } };

type Dispatch = (action: Action) => void;

type State = {
  toasts: ToastProps[];
};

type AddToastOptions = Pick<ToastProps, "type" | "message" | "duration" | "onOpenChange"> & {
  id?: string;
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case "ADD_TOAST": {
      if (state.toasts.some((toast) => toast.id === action.payload.id)) {
        return state;
      }

      return { ...state, toasts: [...state.toasts, action.payload] };
    }

    case "REMOVE_TOAST": {
      return { ...state, toasts: state.toasts.filter((toast) => action.payload.id !== toast.id) };
    }

    default: {
      return state;
    }
  }
}

const ToastContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    toasts: [],
  });

  const memoizedValue = useMemo(() => {
    return { state, dispatch };
  }, [state, dispatch]);

  return (
    <ToastContext.Provider value={memoizedValue}>
      <RadixToast.Provider swipeDirection="down">{children}</RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToasts() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToasts must be used within a ToastProvider");
  }

  const { state, dispatch } = context;

  const addToast = useCallback(
    (options: AddToastOptions) => {
      dispatch({
        type: "ADD_TOAST",
        payload: {
          id: options.id ?? uuidv4(),
          message: options.message,
          type: options.type,
          duration: options.duration ?? 5000,
          onOpenChange: options.onOpenChange,
        },
      });
    },
    [dispatch],
  );

  const removeToast = useCallback(
    (id: string) => {
      dispatch({
        type: "REMOVE_TOAST",
        payload: { id },
      });
    },
    [dispatch],
  );

  return {
    toasts: state.toasts,
    addToast,
    removeToast,
  };
}
